import React from 'react';
import { Button, Input, Spin } from 'antd';
import { AutoSizer, CellMeasurer, CellMeasurerCache, List } from 'react-virtualized';
import { DeleteOutlined, SearchOutlined, WarningOutlined } from '@ant-design/icons';
import { WithTranslation, withTranslation } from 'react-i18next';
import * as utils from '../../../utils/Utils';
import VcdbAddConfigPopover from './VcdbAddConfigPopover';
import VcdbAddTypeConfigPopover from './VcdbAddTypeConfigPopover';
import VcdbAddModelPopover from './VcdbAddModelPopover';
import { DRAWER_RENDER_DELAY_MS } from '../../../constants/TabNavigationConstants';

type ApplicationColumnProps = {
  title: string | React.ReactNode;
  subconfigName?: string;
  scrollToFirstElement?: boolean;
  autoFocus?: boolean;
  rows: any[];
  selected: number[] | null;
  cmdKeyMultiselect?: boolean;
  multiselect?: boolean;
  noRank3?: boolean;
  getRecommendations: boolean;
  fetching?: boolean;
  addCustomValue?: (
    configName: string,
    value: string,
    type?: string,
    group?: string
  ) => Promise<any>;
  deleteCustomValue?: (configName: string, id: number) => Promise<any>;
  filter?: (keywords: string) => void;
  mapConfigToApplication?: (subconfigName: string, rowId: number) => void;
  submit?: (rowId: number | number[], addValues: any, save?: boolean) => void;
  handleClearSelection?: () => void;
} & WithTranslation;

type ApplicationColumnState = {
  data: any[];
  scrolledToFirstElement: boolean;
  scrollToIndex?: number;
  keywords: string;
  keyboardSelectedIndex: number | null;
  columnFocused: boolean;
};

class ApplicationColumn extends React.Component<ApplicationColumnProps, ApplicationColumnState> {
  private filterInput = React.createRef<any>();

  private listRef = React.createRef<List>();

  cache: CellMeasurerCache;

  selectedStart: number | null = null;

  lastSelected: number | null = null;

  constructor(props: ApplicationColumnProps) {
    super(props);
    this.cache = new CellMeasurerCache({ defaultHeight: 30, fixedWidth: true });

    this.state = {
      data: props.rows,
      scrolledToFirstElement: false,
      scrollToIndex: undefined,
      keywords: '',
      keyboardSelectedIndex: null,
      columnFocused: false,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    if (this.props.scrollToFirstElement) {
      const index = this.findFirstRanked();
      if (index) this.scrollToIndex(index);
    }

    if (this.props.autoFocus) {
      this.focusSearchInput();
    }
  }

  componentDidUpdate(prevProps: ApplicationColumnProps, prevState: ApplicationColumnState) {
    const { keywords } = this.state;
    if (this.props.rows !== prevProps.rows) {
      const data = this.filterAndSliceData(keywords);
      this.selectedStart = null;
      this.setState({ data, keyboardSelectedIndex: null });
    }
    if (this.props.scrollToFirstElement && !this.state.scrolledToFirstElement) {
      const index = this.findFirstRanked();
      if (index) this.scrollToIndex(index);
    }
    if (this.state.data !== prevState.data && this.state.data.length === prevState.data.length) {
      this.listRef?.current?.forceUpdateGrid();
      this.cache.clearAll();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = () => {
    this.listRef?.current?.forceUpdateGrid();
    this.cache.clearAll();
  };

  filterAndSliceData = (keywords?: string) => {
    const { rows } = this.props;

    let data = rows;

    if (keywords) {
      data = this.filterRows([...rows], keywords);
    }

    return data;
  };

  findFirstRanked = () => {
    let firstRankIndex: number;
    let firstMappedIndex: number;
    firstRankIndex = 0;
    firstMappedIndex = 0;
    this.props.rows.forEach((row, index) => {
      if (!firstMappedIndex && row.mapped) firstMappedIndex = index;
      if (!firstRankIndex && (row.rank === 1 || row.rank === 2)) firstRankIndex = index;
    });

    return firstMappedIndex || firstRankIndex;
  };

  scrollToIndex = (index: number) => {
    this.setState({ scrolledToFirstElement: true, scrollToIndex: index }, () =>
      this.setState({ scrollToIndex: undefined })
    );
  };

  handleDelete = (id: number) => {
    const { deleteCustomValue, subconfigName } = this.props;
    if (deleteCustomValue) deleteCustomValue(subconfigName!, id);
  };

  handleChangeKeywords = (keywords: string) => {
    this.selectedStart = null;
    this.setState(
      {
        keywords,
        data: this.filterAndSliceData(keywords),
        keyboardSelectedIndex: null,
      },
      () => this.handleResize()
    );
  };

  focusSearchInput = () => {
    setTimeout(() => {
      if (this.filterInput.current?.input) {
        this.filterInput.current.input.focus();
      }
    }, DRAWER_RENDER_DELAY_MS);
  };

  getNavigableInputs = (event: React.KeyboardEvent) => {
    const inputWrapper = (event.target as HTMLElement).closest('.application__input');
    const container =
      inputWrapper?.closest('.drawer-submit__content, .page__content') || document.body;
    const allInputs = Array.from(
      container.querySelectorAll('.application__input input')
    ) as HTMLInputElement[];
    const currentInput = event.target as HTMLInputElement;
    const currentIndex = allInputs.indexOf(currentInput);
    return { allInputs, currentIndex };
  };

  focusNavigableInput = (event: React.KeyboardEvent, direction: 'forward' | 'backward') => {
    const { allInputs, currentIndex } = this.getNavigableInputs(event);
    if (allInputs.length <= 1 || currentIndex === -1) return;

    let nextIndex: number;
    if (direction === 'forward') {
      nextIndex = (currentIndex + 1) % allInputs.length;
    } else {
      nextIndex = currentIndex === 0 ? allInputs.length - 1 : currentIndex - 1;
    }

    allInputs[nextIndex]?.focus();
  };

  handleArrowNavigation = (event: React.KeyboardEvent) => {
    const { data, keyboardSelectedIndex } = this.state;
    const keyboardMultiselect = this.props.multiselect || this.props.cmdKeyMultiselect;

    if (data.length === 0) return;

    event.preventDefault();

    if (event.key === 'ArrowDown') {
      const newIndex =
        keyboardSelectedIndex === null ? 0 : Math.min(keyboardSelectedIndex + 1, data.length - 1);

      if (event.shiftKey && keyboardMultiselect) {
        if (this.selectedStart === null) {
          this.selectedStart = keyboardSelectedIndex === null ? 0 : keyboardSelectedIndex;
        }
      } else {
        this.selectedStart = null;
      }

      this.setState({ keyboardSelectedIndex: newIndex });
    } else if (event.key === 'ArrowUp') {
      if (keyboardSelectedIndex !== null) {
        const newIndex = Math.max(keyboardSelectedIndex - 1, 0);

        if (event.shiftKey && keyboardMultiselect) {
          if (this.selectedStart === null) {
            this.selectedStart = keyboardSelectedIndex;
          }
        } else {
          this.selectedStart = null;
        }

        this.setState({ keyboardSelectedIndex: newIndex });
      }
    } else if (event.key === 'Enter' && keyboardSelectedIndex !== null) {
      const row = data[keyboardSelectedIndex];
      if (row) {
        if (this.selectedStart !== null && keyboardMultiselect) {
          if (this.props.mapConfigToApplication) {
            this.submitKeyboardMultiSelectMapConfig(this.selectedStart, keyboardSelectedIndex);
          } else if (this.props.submit) {
            this.submitKeyboardMultiSelect(this.selectedStart, keyboardSelectedIndex, event);
          }
        } else if (this.props.mapConfigToApplication) {
          this.props.mapConfigToApplication(this.props.subconfigName!, row.id);
        } else if (this.props.submit) {
          const addValues = this.props.cmdKeyMultiselect ? event.ctrlKey || event.metaKey : false;
          this.props.submit(row.id, addValues);
          this.lastSelected = keyboardSelectedIndex;
        }
        this.selectedStart = null;
        this.setState({ keyboardSelectedIndex: null });
        this.focusNextInput(event);
      }
    }
  };

  focusNextInput = (event: React.KeyboardEvent) => {
    this.focusNavigableInput(event, 'forward');
  };

  submitKeyboardMultiSelect = (
    startIndex: number,
    endIndex: number,
    event: React.KeyboardEvent
  ) => {
    const { data } = this.state;
    const { multiselect, cmdKeyMultiselect, submit } = this.props;

    if ((!multiselect && !cmdKeyMultiselect) || !submit) return;

    const minIndex = Math.min(startIndex, endIndex);
    const maxIndex = Math.max(startIndex, endIndex);
    const selectedRows = data.slice(minIndex, maxIndex + 1);
    const selectedIds = selectedRows.filter(row => row?.id).map(row => row.id);

    if (selectedIds.length > 0) {
      const addValues = cmdKeyMultiselect ? event.ctrlKey || event.metaKey : false;
      submit(selectedIds, addValues, true);
      this.lastSelected = endIndex;
    }
  };

  submitKeyboardMultiSelectMapConfig = (startIndex: number, endIndex: number) => {
    const { data } = this.state;
    const { mapConfigToApplication, subconfigName, multiselect, cmdKeyMultiselect } = this.props;

    if ((!multiselect && !cmdKeyMultiselect) || !mapConfigToApplication || !subconfigName) return;

    const minIndex = Math.min(startIndex, endIndex);
    const maxIndex = Math.max(startIndex, endIndex);
    const selectedRows = data.slice(minIndex, maxIndex + 1);

    selectedRows.forEach(row => {
      if (row?.id) {
        mapConfigToApplication(subconfigName, row.id);
      }
    });

    this.lastSelected = endIndex;
  };

  handleTabNavigation = (event: React.KeyboardEvent) => {
    if (event.key !== 'Tab') return;
    event.preventDefault();
    this.focusNavigableInput(event, event.shiftKey ? 'backward' : 'forward');
  };

  handleClick = (row: { id: number }, event: { ctrlKey: any; metaKey: any }, index: number) => {
    const { cmdKeyMultiselect } = this.props;

    if (this.props.mapConfigToApplication) {
      this.props.mapConfigToApplication(this.props.subconfigName!, row.id);
    } else {
      const addValues = cmdKeyMultiselect ? event.ctrlKey || event.metaKey : false;
      if (this.props.submit) this.props.submit(row.id, addValues);
      if (!(addValues && this.props.selected?.includes(row.id))) {
        this.lastSelected = index;
      }
    }
  };

  filterRows = (data: any[], keywords: string) => {
    const keywordsList = utils.stringToArray(keywords.toLowerCase());

    const filteredData = data.filter(element => {
      if (!element.name) return false;
      const name = element.name.toString().toLowerCase();
      let found = true;
      keywordsList.forEach(keyword => {
        if (found) {
          found = name.includes(keyword);
        }
      });
      return found;
    });
    return this.props.subconfigName !== 'positions'
      ? filteredData
      : filteredData.sort((a, b) => {
          if (a.name.length === b.name.length) {
            if (a.name < b.name) return -1;
            if (a.name > b.name) return 1;
            return 0;
          }
          if (a.name.length < b.name.length) return -1;
          if (a.name.length > b.name.length) return 1;
          return 0;
        });
  };

  multiselectRows = (
    index: number,
    event: { buttons: number; ctrlKey: any; metaKey: any },
    save = false
  ) => {
    let selectedRows = [];
    if (this.selectedStart !== null && this.props.multiselect) {
      if (this.selectedStart !== null && this.selectedStart! < index) {
        selectedRows = this.state.data.slice(this.selectedStart!, index + 1);
      } else if (this.selectedStart !== null && this.selectedStart! > index) {
        selectedRows = this.state.data.slice(index, this.selectedStart! + 1);
      } else {
        selectedRows = [this.state.data[index]];
      }

      selectedRows = selectedRows.map(row => row.id);

      if (event.buttons === 0) save = true;
      if (selectedRows.length > 0) {
        if (this.props.submit)
          this.props.submit(selectedRows, event.ctrlKey || event.metaKey, save);
        this.lastSelected = this.selectedStart;
      }
    }
  };

  shiftMultiSelect = (index: number, event: { ctrlKey: any; metaKey: any }) => {
    const { multiselect } = this.props;
    const { data } = this.state;
    let selectedRows = [];
    if (this.lastSelected !== null && multiselect) {
      if (this.lastSelected < index) {
        selectedRows = data.slice(this.lastSelected, index + 1);
      } else if (this.lastSelected > index) {
        selectedRows = data.slice(index, this.lastSelected + 1);
      } else {
        selectedRows = data[index];
      }
      selectedRows = selectedRows.map((row: { id: any }) => row.id);
      if (this.props.submit) this.props.submit(selectedRows, event.ctrlKey || event.metaKey);
    }
  };

  handleInputKeyDown = (event: React.KeyboardEvent) => {
    if (['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) {
      this.handleArrowNavigation(event);
    }
    if (event.key === 'Tab') {
      this.handleTabNavigation(event);
    }
  };

  handleInputFocus = () => {
    this.selectedStart = null;
    this.setState({ keyboardSelectedIndex: null, columnFocused: true });
  };

  handleInputBlur = () => {
    this.selectedStart = null;
    this.setState({ columnFocused: false });
  };

  filter = () => {
    // if props.filter is undefined the rows should be filtered by the frontend
    if (this.props.filter || this.props.filter === undefined) {
      return (
        <div className="application__input">
          <Input
            ref={this.filterInput}
            suffix={<SearchOutlined className="text-gray-700" />}
            onChange={e => this.handleChangeKeywords(e.target.value)}
            onKeyDown={this.handleInputKeyDown}
            onFocus={this.handleInputFocus}
            onBlur={this.handleInputBlur}
            size="small"
            allowClear
          />
        </div>
      );
    }
  };

  renderRow = ({ index, parent, key, style }: any) => {
    const { data, keyboardSelectedIndex, columnFocused } = this.state;

    const row = data[index];

    let cssClasses = row.mapped ? ' mapped' : '';
    let rank = row.rank || 3;
    if (this.props.noRank3 && rank === 3) rank = '';
    if (!this.props.getRecommendations) rank = 0;
    cssClasses += ` rank${rank}`;
    if (rank === 3 && !row.mapped) cssClasses += ' application__row_grey';
    if (row.default) cssClasses += ' application__row_default';

    if (columnFocused) {
      if (this.selectedStart !== null && keyboardSelectedIndex !== null) {
        const minIndex = Math.min(this.selectedStart, keyboardSelectedIndex);
        const maxIndex = Math.max(this.selectedStart, keyboardSelectedIndex);
        if (index >= minIndex && index <= maxIndex) {
          cssClasses += ' application__row_keyboard_selected';
        }
      } else if (keyboardSelectedIndex === index) {
        cssClasses += ' application__row_keyboard_selected';
      }
    }

    return (
      <CellMeasurer cache={this.cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
        <div
          key={key}
          style={style}
          onClick={e =>
            e.shiftKey ? this.shiftMultiSelect(index, e) : this.handleClick(row, e, index)
          }
          onMouseDown={() => (this.selectedStart = index)}
          onMouseUp={e => {
            if (window.getSelection) window.getSelection()?.removeAllRanges(); // don't select text
            if (this.props.submit && this.selectedStart !== index) this.multiselectRows(index, e);
          }}
          onMouseEnter={e => {
            if (this.props.submit && e.buttons === 1) {
              this.multiselectRows(index, e);
              e.preventDefault();
            }
          }}
          className={`row application__row flex ${cssClasses}`}
          data-testid={row.name}
        >
          <div className="application__row_name flex-1">{row.extended_name || row.name}</div>
          {!!this.props.addCustomValue && row.custom_version_ids?.length > 0 && (
            <DeleteOutlined
              className="self-center pr-1"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                this.handleDelete(row.id);
              }}
            />
          )}
          {row.invalid && <WarningOutlined className="self-center pr-1" />}
        </div>
      </CellMeasurer>
    );
  };

  render() {
    const { addCustomValue, subconfigName, fetching, t } = this.props;
    const { data, scrollToIndex, keywords } = this.state;

    return (
      <div className="float__left application__column">
        <div className="application__title flex justify-between">
          {this.props.title}
          {this.props.handleClearSelection && (
            <Button
              size="small"
              disabled={this.props.selected?.length === 0}
              onClick={() => this.props.handleClearSelection!()}
            >
              {t('common:clearSelection')}
            </Button>
          )}
          {addCustomValue && (
            <div className="flex-1 text-right">
              {subconfigName === 'models' && (
                <VcdbAddModelPopover
                  handleSave={(name, type, group) =>
                    addCustomValue(subconfigName!, name, type, group)
                  }
                  keywords={keywords}
                />
              )}
              {subconfigName === 'vehicle_types' && (
                <VcdbAddTypeConfigPopover
                  handleSave={(name, type, group) =>
                    addCustomValue(subconfigName!, name, type, group)
                  }
                  keywords={keywords}
                />
              )}
              {subconfigName !== 'vehicle_types' && subconfigName !== 'models' && (
                <VcdbAddConfigPopover
                  handleSave={name => addCustomValue(subconfigName!, name)}
                  keywords={keywords}
                />
              )}
            </div>
          )}
        </div>
        {this.filter()}
        <div className="application__table">
          <AutoSizer>
            {({ width, height }) => (
              <List
                ref={this.listRef}
                deferredMeasurementCache={this.cache}
                height={height - 2}
                rowCount={data.length}
                rowHeight={this.cache.rowHeight}
                rowRenderer={this.renderRow}
                noRowsRenderer={() => (fetching ? <Spin className="spinner-center" /> : <div />)}
                width={width - 2}
                scrollToIndex={scrollToIndex}
              />
            )}
          </AutoSizer>
        </div>
      </div>
    );
  }
}

export default withTranslation()(ApplicationColumn);
